import { Injectable, computed, inject, signal } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';

export type DeviceType = 'mobile' | 'tablet' | 'desktop';

/**
 * Width (px) at which each device class starts. Mirrors `src/styles/breakpoints.less`,
 * keep the two in sync.
 */
export const DEVICE_BREAKPOINTS = {
  tablet: 600,
  desktop: 1024,
} as const;

const QUERIES: Record<DeviceType, string> = {
  mobile: `(max-width: ${DEVICE_BREAKPOINTS.tablet - 1}px)`,
  tablet: `(min-width: ${DEVICE_BREAKPOINTS.tablet}px) and (max-width: ${DEVICE_BREAKPOINTS.desktop - 1}px)`,
  desktop: `(min-width: ${DEVICE_BREAKPOINTS.desktop}px)`,
};

/**
 * Tells components which device class the viewport falls in. Layout itself is done
 * in CSS (see `breakpoints.less`); use this only for what CSS cannot do, such as
 * changing a component input or opening a dialog differently.
 *
 * The current class is also written to `<html data-device="...">`, so CSS can
 * target it too.
 */
@Injectable({ providedIn: 'root' })
export class ViewportService {
  private breakpoints = inject(BreakpointObserver);

  readonly device = signal<DeviceType>('desktop');
  readonly isMobile = computed(() => this.device() === 'mobile');
  readonly isTablet = computed(() => this.device() === 'tablet');
  readonly isDesktop = computed(() => this.device() === 'desktop');

  constructor() {
    this.breakpoints.observe(Object.values(QUERIES)).subscribe(() => this.update());
  }

  private update(): void {
    const device = (Object.keys(QUERIES) as DeviceType[]).find((type) =>
      this.breakpoints.isMatched(QUERIES[type]),
    );
    if (!device) return;

    this.device.set(device);
    document.documentElement.setAttribute('data-device', device);
  }
}
